import { gql, useQuery } from '@apollo/client';
import { Avatar, Button, Divider, Heading, Link } from '@apollo/orbit';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';
import { proposalHomepageRouteConfig } from '../../routes';
import { ReadmeCard } from '../../variantsHomepage/readmeCard/ReadmeCard';
import { readmeMarkdownServiceFragment } from '../../variantsHomepage/readmeMarkdown/ReadmeMarkdown';
import { CommentMarkdownEditor } from '../CommentMarkdownEditor/CommentMarkdownEditor';
import {
  UseMentionUsersServiceFragment,
  useGetUsersForMentions,
} from '../CommentMarkdownEditor/useMentionUsers';
import { DeleteCommentModal } from '../proposalCommentDrawer/deleteCommentModal/DeleteCommentModal';
import { UseAddCommentProposalFragment } from '../proposalCommentDrawer/shared';
import { useAddProposalComment } from '../proposalCommentDrawer/useAddProposalComment';
import { ProposalStatusBadge } from '../ProposalStatusBadge';

import {
  ActivityFeed,
  ActivityFeedFirstLaunchGraphVariantFragment,
  ActivityFeedProposalFragment,
} from './activityFeed/ActivityFeed';
import { ChangeProposalStatusModal } from './changeProposalStatusModal/ChangeProposalStatusModal';
import { ChecksOverviewSection } from './checksOverviewSection/ChecksOverviewSection';
import {
  ProposalHeaderData,
  ProposalHeaderDataVariantFragment,
} from './ProposalHeaderData';
import { ProposalReviewModalProposalReviewFragment } from './proposalReviewModal/ProposalReviewModal';
import { ProposalStatusDescription } from './proposalStatusDescription/ProposalStatusDescription';
import { ReplaceRequestedReviewersModal } from './requestedReviewersSection/replaceRequestedReviewersModal/ReplaceRequestedReviewersModal';
import {
  RequestedReviewersSection,
  RequestedReviewersSectionProposalFragment,
  RequestedReviewersSectionServiceFragment,
} from './requestedReviewersSection/RequestedReviewersSection';
import {
  SingleDeepLinkedProposalComment,
  SingleDeepLinkedProposalCommentProposalFragment,
} from './singleDeepLinkedProposalComment/SingleDeepLinkedProposalComment';

const ProposalHomepageQuery = gql<
  GraphQLTypes.ProposalHomepageQuery,
  GraphQLTypes.ProposalHomepageQueryVariables
>`
  # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
  query ProposalHomepageQuery(
    $graphId: ID!
    $graphVariant: String!
    $canQueryLaunches: Boolean!
    $threadId: ID!
    $shouldShowDeepLinkedComment: Boolean!
  ) {
    service(id: $graphId) {
      id
      title
      variant(name: $graphVariant) {
        id
        proposal {
          id
          sourceVariant {
            name
            id
          }
          description
          descriptionUpdatedAt
          descriptionUpdatedBy {
            id
            ... on User {
              fullName
            }
            ... on Service {
              id
              title
            }
            ... on InternalIdentity {
              name
            }
          }
          createdBy {
            id
            ... on User {
              fullName
              avatarUrl
            }
          }
          reviews {
            id
            ...ProposalReviewModalProposalReviewFragment
          }
          latestRevision {
            id
            previousRevision {
              id
            }
          }
          canEditProposal
          ...SingleDeepLinkedProposalCommentProposalFragment
            @include(if: $shouldShowDeepLinkedComment)
          ...RequestedReviewersSectionProposalFragment
          ...UseAddCommentProposalFragment
        }
        ...ProposalHeaderDataVariantFragment
      }
      ...RequestedReviewersSectionServiceFragment
      ...ReadmeMarkdownServiceFragment
    }
  }
  ${ProposalHeaderDataVariantFragment}
  ${readmeMarkdownServiceFragment}
  ${RequestedReviewersSectionProposalFragment}
  ${RequestedReviewersSectionServiceFragment}
  ${ProposalReviewModalProposalReviewFragment}
  ${UseAddCommentProposalFragment}
  ${SingleDeepLinkedProposalCommentProposalFragment}
`;

const pageSize = 25;

export const ProposalHomepage = ({
  graphRef,
  canQueryLaunches,
  membershipsData,
}: {
  graphRef: GraphRef;
  canQueryLaunches: boolean;
  membershipsData: GraphQLTypes.ReviewerMultiselectPopoverUsersServiceFragment | null;
}) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { focusThreadId: threadId } = useRouteParams(
    proposalHomepageRouteConfig,
  );
  const location = useLocation();

  const shouldShowDeepLinkedComment = !!threadId;
  const { data, error, loading } = useQuery(ProposalHomepageQuery, {
    variables: {
      ...graphRef,
      canQueryLaunches,
      // If we don't have a thread ID, the query that requires this field to be non nullable is skipped
      threadId: threadId ?? '',
      shouldShowDeepLinkedComment,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const {
    data: activityFeedData,
    loading: activityFeedLoading,
    fetchMore: fetchMoreActivities,
  } = useQuery<
    GraphQLTypes.ProposalHomepageActivityFeedQuery,
    GraphQLTypes.ProposalHomepageActivityFeedQueryVariables
  >(
    gql`
      query ProposalHomepageActivityFeedQuery(
        $graphId: ID!
        $graphVariant: String!
        $after: String
        $first: Int
        $canQueryLaunches: Boolean!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            proposal {
              id
              ...ActivityFeedProposalFragment
              ...UseAddCommentProposalFragment
            }
            ...ActivityFeedFirstLaunchGraphVariantFragment
          }
        }
      }
      ${ActivityFeedProposalFragment}
      ${ActivityFeedFirstLaunchGraphVariantFragment}
      ${UseAddCommentProposalFragment}
    `,
    {
      variables: {
        ...graphRef,
        first: pageSize,
        canQueryLaunches,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      skip: shouldShowDeepLinkedComment,
    },
  );

  const graph = data?.service;
  const variant = graph?.variant;
  const proposal = variant?.proposal;
  const canEditProposal = proposal?.canEditProposal ?? false;
  const activities = activityFeedData?.service?.variant?.proposal?.activities;
  const currentActivityFeedLength = activities?.edges?.length ?? 0;
  const totalActivityFeedLength = activities?.totalCount ?? 0;

  const { data: graphForMentions } = useQuery(
    gql<
      GraphQLTypes.ProposalHomepageGraphForMentions,
      GraphQLTypes.ProposalHomepageGraphForMentionsVariables
    >`
      query ProposalHomepageGraphForMentions($graphId: ID!) {
        service(id: $graphId) {
          id
          ...UseMentionUsersServiceFragment
        }
      }
      ${UseMentionUsersServiceFragment}
    `,
    {
      variables: graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const usersForMentions = useGetUsersForMentions(graphForMentions?.service);
  const {
    addComment,
    loading: addCommentLoading,
    commentErrorMessage,
    handleAddCommentResult,
  } = useAddProposalComment({
    proposal,
    users: usersForMentions.users,
    graphRef,
  });

  const handleSubmitComment = useCallback(
    async (getValue: () => string, setValue: (value: string) => void) => {
      const value = getValue();
      const result = await addComment({ message: value });

      handleAddCommentResult({
        result,
        onSuccess: () => {
          setValue('');
        },
      });
    },
    [addComment, handleAddCommentResult],
  );

  if (loading) {
    return <Loading />;
  } else if (!graph || !variant || !proposal || error) {
    return <ErrorMessage error={error} />;
  }
  const author = variant.proposal?.createdBy;
  const sourceGraphVariant = variant.proposal?.sourceVariant.name;

  return (
    <>
      <ProposalHeaderData
        className="py-3 pl-8 pr-2"
        variant={variant}
        graphId={graphRef.graphId}
      />
      <div className="grid grid-cols-[minmax(500px,2fr)_minmax(100px,300px)] gap-6 px-8 pb-6 max-lg:grid-cols-1 ">
        <div>
          <ReadmeCard
            graphRef={graphRef}
            readme={{
              content: proposal.description,
              id: 'no-id-in-proposal-description',
              lastUpdatedTime: proposal.descriptionUpdatedAt,
              lastUpdatedBy: proposal.descriptionUpdatedBy,
              __typename: 'Readme',
            }}
            shortcodesData={graph}
            loading={loading}
            className="mb-8 mt-4"
            canEditProposal={canEditProposal}
          />
          <h2 className="text-xl font-semibold">Proposal activity</h2>
          {shouldShowDeepLinkedComment ? (
            <SingleDeepLinkedProposalComment
              proposal={proposal}
              graphRef={graphRef}
              usersForMentions={usersForMentions}
            />
          ) : (
            <>
              {commentErrorMessage !== null && (
                <ErrorMessage className="mt-3">
                  {commentErrorMessage}
                </ErrorMessage>
              )}
              <CommentMarkdownEditor
                usersForMentions={usersForMentions}
                className="mt-3"
                actions={({ hasComment, getValue, setValue }) => {
                  return (
                    <Button
                      size="sm"
                      isLoading={addCommentLoading}
                      loadingText="Commenting"
                      isDisabled={!hasComment}
                      className="w-fit min-w-0"
                      onClick={() => handleSubmitComment(getValue, setValue)}
                    >
                      Comment
                    </Button>
                  );
                }}
              />
              <Divider className="my-6" />
              {activityFeedLoading ? (
                <Loading />
              ) : (
                activities &&
                activityFeedData.service?.variant &&
                activityFeedData.service.variant.proposal && (
                  <>
                    <ActivityFeed
                      graphRef={graphRef}
                      proposal={activityFeedData.service.variant.proposal}
                      variant={activityFeedData.service.variant}
                      usersForMentions={usersForMentions}
                      canQueryLaunches={canQueryLaunches}
                      sourceGraphVariant={sourceGraphVariant}
                    />
                    {activities.pageInfo.hasNextPage &&
                      isMinLengthArray(1, activities.edges) && (
                        <div className="mt-4 flex w-full justify-center">
                          <Button
                            variant="secondary"
                            isLoading={isFetchingMore}
                            loadingText="Loading more items"
                            onClick={async () => {
                              setIsFetchingMore(true);
                              await fetchMoreActivities({
                                variables: {
                                  after: activities.pageInfo.endCursor,
                                },
                              });
                              setIsFetchingMore(false);
                            }}
                          >
                            Load{' '}
                            {Math.min(
                              pageSize,
                              Math.max(
                                0,
                                (totalActivityFeedLength ??
                                  currentActivityFeedLength - pageSize) -
                                  currentActivityFeedLength,
                              ),
                            )}{' '}
                            more items
                          </Button>
                        </div>
                      )}
                  </>
                )
              )}
            </>
          )}
        </div>

        <div className="mt-4 space-y-6">
          <div>
            <div className="mb-3 flex items-center justify-between border-b border-b-primary pb-2">
              <Heading size="lg" className="flex items-center">
                Status{' '}
                <ProposalStatusBadge
                  className="ml-2"
                  status={proposal.status}
                />
              </Heading>
              {canEditProposal &&
                proposal.status !== ProposalStatus.IMPLEMENTED && (
                  <Link
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.editProposalStatus,
                    })}
                    className="text-sm"
                  >
                    Edit status
                  </Link>
                )}
            </div>
            <p className="text-base text-secondary">
              <ProposalStatusDescription status={proposal.status} />
            </p>
          </div>
          <div>
            <Heading
              size="lg"
              className="mb-3 flex justify-between border-b border-b-primary pb-2"
            >
              Author
            </Heading>
            <div className="flex items-center text-base">
              {author?.__typename === 'User' ? (
                <>
                  <Avatar
                    size="sm"
                    name={author.fullName}
                    src={author.avatarUrl ?? undefined}
                    className="mr-2"
                  />
                  {author.fullName}
                </>
              ) : (
                <>
                  <Avatar size="sm" className="mr-2" />
                  Unknown
                </>
              )}
            </div>
          </div>
          <RequestedReviewersSection
            proposal={proposal}
            graph={graph}
            canEditProposal={canEditProposal}
            membershipsData={membershipsData}
          />
          {variant.proposal?.latestRevision &&
            !!variant.proposal.latestRevision.previousRevision &&
            canQueryLaunches && (
              <ChecksOverviewSection
                latestRevisionId={variant.proposal.latestRevision.id}
              />
            )}
        </div>
      </div>
      <LazyUrlModal name={Config.modals.editProposalStatus}>
        {({ closeModal, isOpen }) => (
          <ChangeProposalStatusModal
            proposalId={proposal.id}
            isOpen={isOpen}
            onClose={closeModal}
            currentStatus={proposal.status}
          />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.deleteProposalComment}>
        {({ closeModal, isOpen }) => (
          <DeleteCommentModal
            proposalId={proposal.id}
            graphRef={graphRef}
            isOpen={isOpen}
            onClose={() =>
              closeModal(undefined, ['proposalCommentId', 'commentThreadId'])
            }
          />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.replaceRequestedReviewers}>
        {({ closeModal, isOpen }) => (
          <ReplaceRequestedReviewersModal
            proposalId={proposal.id}
            defaultReviewers={graph.defaultProposalReviewers}
            isOpen={isOpen}
            onClose={closeModal}
          />
        )}
      </LazyUrlModal>
    </>
  );
};
